/**
 * @fileoverview
 * Dropdown Component - Custom dropdown with single/multi-select
 * Migrated to new architecture with OnPush change detection
 *
 * @version 1.0.0
 * @author KATALYST TEAM
 */

import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  Renderer2,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ViewChild,
  inject,
} from '@angular/core';
import { DropdownService } from '../../../@core/services/dropdown.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { debounce } from '@core/utils';

@Component({
  selector: 'app-dropdown',
  standalone: true,
  imports: [CommonModule, FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './dropdown.component.html',
  styleUrl: './dropdown.component.css',
})
export class DropdownComponent implements OnInit, OnDestroy, OnChanges {
  private readonly dropdownService = inject(DropdownService);
  private readonly elementRef = inject(ElementRef);
  private readonly renderer = inject(Renderer2);
  private readonly cdr = inject(ChangeDetectorRef);

  @Input() label: string = '';
  @Input() showAsterisk: boolean = false;
  @Input() options: string[] = [];
  /** When set, options are shown in groups with labels (group label before each group). */
  @Input() optionGroups?: { label: string; options: string[] }[] = undefined;
  @Input() multiSelect: boolean = false;
  @Input() placeholder: string = 'Select an option';
  @Input() selectedValues: string[] = [];
  @Input() selectedOption: string = '';
  @Input() buttonClass: string = '';
  @Input() listItemClass: string = '';
  @Input() errorMessage: string | null = null;
  @Input() showError: boolean = false;
  @Input() disabled: boolean = false;
  @Input() enableSearch: boolean = false;
  /** When true (single-select only), show a clear (×) control inside the field between the value and the caret. */
  @Input() showClearButton: boolean = false;
  /** When true, panel opens above the trigger. When 'auto', opens upward when there is not enough space below. */
  @Input() openUpward: boolean | 'auto' = false;
  /** Max height of the list in px. When not set, a default Tailwind class shows ~5-6 entries. */
  @Input() maxHeight: number | null = null;
  /** Optional: option value -> main label (e.g. show column name instead of "table.column"). */
  @Input() optionDisplay: Record<string, string> | null = null;
  /** Optional: option value -> badge text (e.g. table name shown as badge after label). */
  @Input() optionBadges: Record<string, string> | null = null;
  @Output() selectionChange = new EventEmitter<string | string[]>();
  @Output() searchChange = new EventEmitter<string>();
  @Output() dropdownOpen = new EventEmitter<void>();

  @ViewChild('searchInput', { static: false }) searchInput?: ElementRef<HTMLInputElement>;
  @ViewChild('dropdownTrigger', { static: false }) dropdownTrigger?: ElementRef<HTMLButtonElement>;
  @ViewChild('dropdownMenu', { static: false }) dropdownMenu?: ElementRef<HTMLDivElement>;

  /** Unique id for the trigger button so the label can be associated with it (accessibility). */
  protected triggerId!: string;

  private static idCounter = 0;

  isOpen: boolean = false;
  searchQuery: string = '';
  filteredOptions: string[] = [];
  /** Set when openUpward === 'auto' and there is insufficient space below; cleared on close. */
  private openUpwardDetected: boolean = false;
  private globalClickListener: (() => void) | null = null;

  /** Whether the panel should open upward (from input or auto-detected when openUpward is 'auto'). */
  get effectiveOpenUpward(): boolean {
    return this.openUpward === true || (this.openUpward === 'auto' && this.openUpwardDetected);
  }

  /** Flattened list of selectable options (from optionGroups or options). */
  get selectableOptions(): string[] {
    if (this.optionGroups?.length) {
      return this.optionGroups.flatMap((g) => g.options);
    }
    return this.options;
  }
  private lastEmittedSearchQuery: string = '';
  // Create debounced search emit function using performance utils (300ms delay)
  private readonly debouncedSearchEmit = debounce((query: string) => {
    this.lastEmittedSearchQuery = query;
    this.searchChange.emit(query);
  }, 300);

  ngOnChanges(changes: SimpleChanges) {
    if (changes['options'] || changes['optionGroups'] || changes['enableSearch']) {
      this.updateFilteredOptions();
      this.cdr.markForCheck();
    }
    if (changes['selectedOption'] && !changes['selectedOption'].firstChange) {
      this.selectedOption = changes['selectedOption'].currentValue ?? '';
      this.cdr.markForCheck();
    }
  }

  ngOnInit() {
    this.triggerId = `dropdown-trigger-${++DropdownComponent.idCounter}`;
    this.updateFilteredOptions();
    this.globalClickListener = this.renderer.listen(
      'document',
      'click',
      (event: Event) => {
        // Early return if dropdown is not open - avoid expensive DOM checks
        if (!this.isOpen) {
          return;
        }

        const target = event.target as HTMLElement;

        // Skip processing clicks on buttons - they handle their own events
        // This improves performance when clicking modal action buttons (cancel/save)
        if (target.tagName === 'BUTTON' || target.closest('button')) {
          return;
        }

        const nativeElement = this.elementRef.nativeElement;

        // Quick check: if click is inside dropdown element, do nothing
        if (nativeElement.contains(target)) {
          return;
        }

        // Check if click is on dropdown menu (which might be outside nativeElement due to positioning)
        if (target.closest('.form-select-dropdown-list')) {
          return;
        }

        // Click is outside - close dropdown
        this.dropdownService.closeAllDropdowns();
      }
    );
  }

  onSelect(option: string) {
    this.selectionChange.emit(option);
  }

  toggleDropdown(event: Event) {
    if (this.disabled) {
      return;
    }
    event.stopPropagation();
    event.preventDefault();
    if (this.isOpen) {
      this.isOpen = false;
      this.openUpwardDetected = false;
      this.cdr.markForCheck();
      return;
    }
    // Opening: let parent run dropdownOpen and update openUpward before we paint the menu
    // to avoid flicker (menu first with old openUpward, then re-render with new).
    this.dropdownService.setOpenDropdown(this);
    this.searchQuery = '';
    this.updateFilteredOptions();
    this.dropdownOpen.emit();
    if (this.openUpward === 'auto') {
      const el = this.elementRef.nativeElement as HTMLElement;
      const rect = el.getBoundingClientRect();
      const gap = 8;
      const minSpaceBelow = 220;
      this.openUpwardDetected = window.innerHeight - rect.bottom - gap < minSpaceBelow;
    } else {
      this.openUpwardDetected = this.openUpward === true;
    }
    setTimeout(() => {
      this.isOpen = true;
      this.cdr.markForCheck();
    }, 0);
  }

  onOptionKeydown(event: KeyboardEvent, option: string): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.selectOption(option);
    }
  }

  selectOption(option: string) {
    if (this.disabled || this.isGroupLabel(option)) {
      return;
    }
    if (this.multiSelect) {
      if (this.selectedValues.includes(option)) {
        this.selectedValues = this.selectedValues.filter(
          (item) => item !== option
        );
      } else {
        this.selectedValues.push(option);
      }
      this.selectionChange.emit(this.selectedValues);
    } else {
      this.selectedOption = option;
      this.selectionChange.emit(option);
      // Close dropdown immediately before emitting to prevent delay
      this.isOpen = false;
      this.dropdownService.setOpenDropdown(null);
      this.cdr.markForCheck();
    }
  }

  isSelected(option: string): boolean {
    return this.multiSelect
      ? this.selectedValues.includes(option)
      : this.selectedOption === option;
  }

  /** For multiSelect trigger: show optionDisplay label when available, else raw value. */
  getMultiSelectDisplayValues(values: string[]): string[] {
    if (!this.optionDisplay) return values;
    return values.map((v) => this.optionDisplay?.[v] ?? v);
  }
  
  /** True when option is a group header (non-selectable). */
  isGroupLabel(option: string): boolean {
    if (!this.optionGroups?.length) return false;
    return this.optionGroups.some((g) => g.label === option);
  }

  closeDropdown() {
    // Only update if actually open to avoid unnecessary change detection
    if (this.isOpen) {
      this.isOpen = false;
      this.openUpwardDetected = false;
      this.searchQuery = '';
      this.lastEmittedSearchQuery = ''; // Reset last emitted query
      this.updateFilteredOptions();
      this.dropdownService.setOpenDropdown(null);
      this.cdr.markForCheck();
    }
  }

  onSearchChange(query: string) {
    // Check if search input has focus before change detection
    const searchInputElement = this.searchInput?.nativeElement;
    const hadFocus = searchInputElement === document.activeElement;
    const cursorPosition = searchInputElement?.selectionStart ?? 0;
    
    this.searchQuery = query;
    this.updateFilteredOptions(); // Update filtered options immediately for UI responsiveness
    
    // Emit search change with debounce (for API calls)
    if (this.enableSearch) {
      this.debouncedSearchEmit(query);
    }
    
    this.cdr.markForCheck();
    
    // Restore focus and cursor position after change detection completes
    if (hadFocus && this.isOpen && searchInputElement) {
      // Use setTimeout to ensure focus happens after the current change detection cycle
      setTimeout(() => {
        const input = this.searchInput?.nativeElement;
        if (input && this.isOpen) {
          input.focus();
          // Restore cursor position
          const newLength = input.value.length;
          const position = Math.min(cursorPosition, newLength);
          input.setSelectionRange(position, position);
        }
      }, 0);
    }
  }

  updateFilteredOptions() {
    const source = this.selectableOptions;
    if (!this.enableSearch || !this.searchQuery.trim()) {
      this.filteredOptions = source;
    } else {
      const query = this.searchQuery.toLowerCase().trim();
      this.filteredOptions = source.filter((option) => {
        const label = this.optionDisplay?.[option] ?? option;
        const labelStr = typeof label === 'string' ? label : String(label ?? '');
        const optionStr = typeof option === 'string' ? option : String(option ?? '');
        return labelStr.toLowerCase().includes(query) || optionStr.toLowerCase().includes(query);
      });
    }
  }

  /**
   * Get button classes based on error state
   */
  getButtonClasses(): string {
    const baseClasses =
      'w-full bg-[#F8F8FE] border text-[12px] text-gray-700 py-2 px-2 rounded-[5px] flex justify-between items-center focus:shadow focus:shadow-[#F0F0F0]';
    const buttonClass = this.buttonClass || '';

    if (this.showError && this.errorMessage) {
      return `${baseClasses} ${buttonClass} border-red-500 focus:border-red-500`.trim();
    }

    return `${baseClasses} ${buttonClass} border-[#DBE0E6] focus:border-[#C7B1FF]`.trim();
  }

  /** Outer shell when using inline clear + caret (single-select, showClearButton). */
  getClearableTriggerClasses(): string {
    const baseClasses =
      'w-full bg-[#F8F8FE] border text-[12px] text-gray-700 rounded-[5px] flex items-stretch focus-within:shadow focus-within:shadow-[#F0F0F0] overflow-hidden';
    const buttonClass = this.buttonClass || '';

    if (this.showError && this.errorMessage) {
      return `${baseClasses} ${buttonClass} border-red-500 focus-within:border-red-500`.trim();
    }

    return `${baseClasses} ${buttonClass} border-[#DBE0E6] focus-within:border-[#C7B1FF]`.trim();
  }

  /** Clear selection from inline control; does not open the list. */
  onTriggerClear(event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    if (this.disabled || this.multiSelect || !this.selectedOption) {
      return;
    }
    this.selectedOption = '';
    this.selectionChange.emit('');
    if (this.isOpen) {
      this.isOpen = false;
      this.openUpwardDetected = false;
      this.dropdownService.setOpenDropdown(null);
    }
    this.cdr.markForCheck();
  }

  ngOnDestroy() {
    if (this.globalClickListener) {
      this.globalClickListener();
    }
  }
}
